/**
 * Typed API client. All requests go through the /api/v1 prefix, which is
 * proxied by Vite to the backend in dev, and served directly in production.
 */
import { ApiError } from './queryClient.js';
import { CREDIT_COSTS } from '@resume-builder/shared';
import type {
  SignupRequest,
  InitiateSignupRequest,
  CompleteSignupRequest,
  LoginRequest,
  AuthResponse,
  User,
  ViewPreferences,
  UpdateViewPreferences,
  Tenure,
  CreateTenure,
  UpdateTenure,
  Title,
  CreateTitle,
  UpdateTitle,
  ReorderTitleItem,
  Experience,
  CreateExperience,
  UpdateExperience,
  ReorderExperienceItem,
  Capability,
  CreateCapability,
  UpdateCapability,
  ReorderItem,
  CapabilityFilters,
  SkillBucket,
  SkillBucketListResponse,
  CreateSkillBucket,
  UpdateSkillBucket,
  AssignSkill,
  ReorderSkillBucket,
  ReorderSkillBucketItem,
  JobPosting,
  IngestJobPostingResponse,
  Resume,
  CreateResume,
  CustomSectionSpec,
  ResumeDisplayOptions,
  UpdateResumeDisplayOptions,
  ResumeBulletRow,
  AvailableCapability,
  Education,
  CreateEducation,
  UpdateEducation,
  ReorderEducationItem,
  UserPlan,
  CreditLedgerEntry,
  Application,
  ApplicationAction,
  ApplicationEvent,
  CreateApplication,
  UpdateApplication,
  UpdateApplicationEvent,
} from '@resume-builder/shared';
import type { SkillGroup } from './skillRepository.js';

const BASE_URL = '/api/v1';

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const contentTypeHeader = options?.body ? { 'Content-Type': 'application/json' } : {};
  const res = await fetch(`${BASE_URL}${path}`, {
    credentials: 'include',
    ...options,
    headers: {
      ...options?.headers,
      ...contentTypeHeader,
    } as HeadersInit,
  });

  if (!res.ok) {
    let errorBody: { error?: string; message?: string } = {};
    try {
      errorBody = (await res.json()) as { error?: string; message?: string };
    } catch {
      // ignore parse error
    }
    throw new ApiError(
      res.status,
      errorBody.error ?? 'Error',
      errorBody.message ?? `Request failed with status ${res.status}`,
    );
  }

  if (res.status === 204) return null as T;
  return res.json() as Promise<T>;
}

// ── Auth ───────────────────────────────────────────────────────────────────────

export const authApi = {
  // 2-step email signup
  initiateSignup: (data: InitiateSignupRequest): Promise<{ message: string }> =>
    request<{ message: string }>('/auth/initiate-signup', { method: 'POST', body: JSON.stringify(data) }),

  verifySignupToken: (token: string): Promise<{ email: string }> =>
    request<{ email: string }>(`/auth/verify-signup?token=${encodeURIComponent(token)}`),

  completeSignup: (data: CompleteSignupRequest): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/complete-signup', { method: 'POST', body: JSON.stringify(data) }),

  // Legacy direct signup (kept for compat)
  signup: (data: SignupRequest): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/signup', { method: 'POST', body: JSON.stringify(data) }),

  login: (data: LoginRequest): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/login', { method: 'POST', body: JSON.stringify(data) }),

  me: (): Promise<AuthResponse> => request<AuthResponse>('/auth/me'),

  updateViewPreferences: (prefs: ViewPreferences): Promise<AuthResponse> =>
    request<AuthResponse>('/auth/me/view-preferences', {
      method: 'PATCH',
      body: JSON.stringify({ viewPreferences: prefs } satisfies UpdateViewPreferences),
    }),

  logout: (): Promise<null> => request<null>('/auth/logout', { method: 'POST' }),
};

// ── Tenures ────────────────────────────────────────────────────────────────────

export const tenureApi = {
  list: (): Promise<Tenure[]> => request<Tenure[]>('/tenures'),

  get: (id: string): Promise<Tenure> => request<Tenure>(`/tenures/${id}`),

  create: (data: CreateTenure): Promise<Tenure> =>
    request<Tenure>('/tenures', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: UpdateTenure): Promise<Tenure> =>
    request<Tenure>(`/tenures/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string): Promise<null> =>
    request<null>(`/tenures/${id}`, { method: 'DELETE' }),
};

// ── Titles ─────────────────────────────────────────────────────────────────────

export const titleApi = {
  list: (tenureId?: string): Promise<Title[]> => {
    const qs = tenureId ? `?tenureId=${tenureId}` : '';
    return request<Title[]>(`/titles${qs}`);
  },

  get: (id: string): Promise<Title> => request<Title>(`/titles/${id}`),

  create: (data: CreateTitle): Promise<Title> =>
    request<Title>('/titles', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: UpdateTitle): Promise<Title> =>
    request<Title>(`/titles/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string): Promise<null> =>
    request<null>(`/titles/${id}`, { method: 'DELETE' }),

  reorder: (reorders: ReorderTitleItem[]): Promise<Title[]> =>
    request<Title[]>('/titles/reorder', {
      method: 'POST',
      body: JSON.stringify({ reorders }),
    }),
};

// ── Experiences ────────────────────────────────────────────────────────────────

export const experienceApi = {
  list: (): Promise<Experience[]> => request<Experience[]>('/experiences'),

  get: (id: string): Promise<Experience> => request<Experience>(`/experiences/${id}`),

  create: (data: CreateExperience): Promise<Experience> =>
    request<Experience>('/experiences', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: UpdateExperience): Promise<Experience> =>
    request<Experience>(`/experiences/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string): Promise<null> =>
    request<null>(`/experiences/${id}`, { method: 'DELETE' }),

  reorder: (reorders: ReorderExperienceItem[]): Promise<Experience[]> =>
    request<Experience[]>('/experiences/reorder', {
      method: 'POST',
      body: JSON.stringify({ reorders }),
    }),
};

// ── Capabilities ───────────────────────────────────────────────────────────────

export interface CapabilityListResponse {
  items: Capability[];
  total: number;
}

export const capabilityApi = {
  list: (filters?: Partial<CapabilityFilters>): Promise<CapabilityListResponse> => {
    const params = new URLSearchParams();
    if (filters?.experienceId) params.set('experienceId', filters.experienceId);
    if (filters?.q) params.set('q', filters.q);
    if (filters?.skill) params.set('skill', filters.skill);
    if (filters?.limit !== undefined) params.set('limit', String(filters.limit));
    if (filters?.offset !== undefined) params.set('offset', String(filters.offset));
    const qs = params.toString();
    return request<CapabilityListResponse>(`/capabilities${qs ? `?${qs}` : ''}`);
  },

  create: (data: CreateCapability): Promise<Capability> =>
    request<Capability>('/capabilities', { method: 'POST', body: JSON.stringify(data) }),

  bulkCreate: (experienceId: string, text: string): Promise<Capability[]> =>
    request<Capability[]>('/capabilities/bulk', {
      method: 'POST',
      body: JSON.stringify({ experienceId, text }),
    }),

  update: (id: string, data: UpdateCapability): Promise<Capability> =>
    request<Capability>(`/capabilities/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string): Promise<null> =>
    request<null>(`/capabilities/${id}`, { method: 'DELETE' }),

  refreshEmbedding: (id: string): Promise<{ message: string }> =>
    request<{ message: string }>(`/capabilities/${id}/refresh-embedding`, { method: 'POST' }),

  getRewriteSuggestions: (id: string): Promise<{
    xyzFormat: { suggestion: string; comment: string };
    carFormat: { suggestion: string; comment: string };
    suggestedSkills?: string[];
  }> =>
    request<{
      xyzFormat: { suggestion: string; comment: string };
      carFormat: { suggestion: string; comment: string };
      suggestedSkills?: string[];
    }>('/capabilities/rewrite', { method: 'POST', body: JSON.stringify({ id }) }),

  regenerateRewriteSuggestion: (
    id: string,
    format: 'xyz' | 'car',
    feedback: string,
  ): Promise<{
    xyzFormat?: { suggestion: string; comment: string };
    carFormat?: { suggestion: string; comment: string };
  }> =>
    request<{
      xyzFormat?: { suggestion: string; comment: string };
      carFormat?: { suggestion: string; comment: string };
    }>('/capabilities/rewrite/regenerate', {
      method: 'POST',
      body: JSON.stringify({ id, format, feedback }),
    }),

  reorder: (reorders: ReorderItem[]): Promise<Capability[]> =>
    request<Capability[]>('/capabilities/reorder', {
      method: 'POST',
      body: JSON.stringify({ reorders }),
    }),
};

// ── Skill Buckets ──────────────────────────────────────────────────────────────

export const skillBucketApi = {
  list: (): Promise<SkillBucketListResponse> =>
    request<SkillBucketListResponse>('/skill-buckets'),

  create: (data: CreateSkillBucket): Promise<SkillBucket> =>
    request<SkillBucket>('/skill-buckets', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: UpdateSkillBucket): Promise<SkillBucket> =>
    request<SkillBucket>(`/skill-buckets/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string): Promise<null> =>
    request<null>(`/skill-buckets/${id}`, { method: 'DELETE' }),

  reorder: (reorders: ReorderSkillBucketItem[]): Promise<null> =>
    request<null>('/skill-buckets/reorder', {
      method: 'POST',
      body: JSON.stringify({ reorders }),
    }),

  assignSkill: (bucketId: string, skill: string): Promise<null> =>
    request<null>(`/skill-buckets/${bucketId}/assign-skill`, {
      method: 'POST',
      body: JSON.stringify({ skill }),
    }),

  unassignSkill: (skill: string): Promise<null> =>
    request<null>('/skill-buckets/unassign-skill', {
      method: 'POST',
      body: JSON.stringify({ skill }),
    }),

  reorderSkills: (bucketId: string, skillOrder: string[]): Promise<null> =>
    request<null>(`/skill-buckets/${bucketId}/reorder-skills`, {
      method: 'POST',
      body: JSON.stringify({ skillOrder }),
    }),
};

// ── Job Postings ───────────────────────────────────────────────────────────────

export const jobPostingApi = {
  ingest: (data: { url?: string; rawText?: string }): Promise<IngestJobPostingResponse> =>
    request<IngestJobPostingResponse>('/postings/ingest', {
      method: 'POST',
      body: JSON.stringify(data),
    }),

  list: (): Promise<JobPosting[]> => request<JobPosting[]>('/postings'),

  getById: (id: string): Promise<JobPosting> => request<JobPosting>(`/postings/${id}`),

  delete: (id: string): Promise<null> =>
    request<null>(`/postings/${id}`, { method: 'DELETE' }),
};

// ── Resumes ────────────────────────────────────────────────────────────────────

export type ExportFormat = 'pdf' | 'docx' | 'md';

export const resumeApi = {
  list: (): Promise<Resume[]> => request<Resume[]>('/resumes'),

  get: (id: string): Promise<Resume> => request<Resume>(`/resumes/${id}`),

  create: (postingId: string, opts?: { customSections?: CustomSectionSpec[]; strategy?: CreateResume['strategy'] }): Promise<Resume> =>
    request<Resume>('/resumes', {
      method: 'POST',
      body: JSON.stringify({ postingId, customSections: opts?.customSections, strategy: opts?.strategy ?? 'standard' } satisfies CreateResume),
    }),

  delete: (id: string): Promise<null> =>
    request<null>(`/resumes/${id}`, { method: 'DELETE' }),

  updateDisplayOptions: (id: string, collapsedTenures: string[]): Promise<Resume> =>
    request<Resume>(`/resumes/${id}/display-options`, {
      method: 'PATCH',
      body: JSON.stringify({ displayOptions: { collapsedTenures } } satisfies UpdateResumeDisplayOptions),
    }),

  /**
   * Fetch a rendered export from the backend and trigger a browser download.
   * Uses fetch + Blob so session cookies are forwarded correctly.
   */
  download: async (id: string, format: ExportFormat): Promise<void> => {
    const res = await fetch(`${BASE_URL}/resumes/${id}/export?format=${format}`, {
      credentials: 'include',
    });

    if (!res.ok) {
      let msg = `Export failed (${res.status})`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) msg = body.error;
      } catch {
        // ignore
      }
      throw new Error(msg);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    // Try to get the filename from Content-Disposition
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    const ext = format === 'md' ? 'md' : format;
    a.download = match?.[1] ?? `resume.${ext}`;
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  createManual: (postingId: string, capabilityIds: string[], educationIds?: string[], skillGroups?: SkillGroup[]): Promise<Resume> =>
    request<Resume>('/resumes/manual', {
      method: 'POST',
      body: JSON.stringify({ postingId, capabilityIds, educationIds, skillGroups }),
    }),

  getBullets: (resumeId: string): Promise<ResumeBulletRow[]> =>
    request<ResumeBulletRow[]>(`/resumes/${resumeId}/bullets`),

  updateBullet: (resumeId: string, bulletId: string, text: string): Promise<ResumeBulletRow> =>
    request<ResumeBulletRow>(`/resumes/${resumeId}/bullets/${bulletId}`, {
      method: 'PATCH',
      body: JSON.stringify({ text }),
    }),

  toggleBullet: (resumeId: string, bulletId: string): Promise<ResumeBulletRow> =>
    request<ResumeBulletRow>(`/resumes/${resumeId}/bullets/${bulletId}/toggle`, {
      method: 'PATCH',
    }),

  deleteBullet: (resumeId: string, bulletId: string): Promise<null> =>
    request<null>(`/resumes/${resumeId}/bullets/${bulletId}`, { method: 'DELETE' }),

  addBullet: (resumeId: string, capabilityId: string): Promise<ResumeBulletRow> =>
    request<ResumeBulletRow>(`/resumes/${resumeId}/bullets`, {
      method: 'POST',
      body: JSON.stringify({ capabilityId }),
    }),

  getAvailableCapabilities: (resumeId: string): Promise<AvailableCapability[]> =>
    request<AvailableCapability[]>(`/resumes/${resumeId}/available-capabilities`),

  rewriteBullet: (resumeId: string, bulletId: string): Promise<{ xyzFormat?: { suggestion: string; comment: string }; carFormat?: { suggestion: string; comment: string }; suggestedSkills?: string[] }> =>
    request(`/resumes/${resumeId}/bullets/${bulletId}/rewrite`, { method: 'POST' }),

  rewriteBulletRegenerate: (resumeId: string, bulletId: string, format: 'xyz' | 'car', feedback: string): Promise<{ xyzFormat?: { suggestion: string; comment: string }; carFormat?: { suggestion: string; comment: string }; suggestedSkills?: string[] }> =>
    request(`/resumes/${resumeId}/bullets/${bulletId}/rewrite/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ format, feedback }),
    }),
};

// ── Education ──────────────────────────────────────────────────────────────────

export const educationApi = {
  list: (): Promise<Education[]> => request<Education[]>('/education'),

  get: (id: string): Promise<Education> => request<Education>(`/education/${id}`),

  create: (data: CreateEducation): Promise<Education> =>
    request<Education>('/education', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: UpdateEducation): Promise<Education> =>
    request<Education>(`/education/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string): Promise<null> =>
    request<null>(`/education/${id}`, { method: 'DELETE' }),

  reorder: (reorders: ReorderEducationItem[]): Promise<Education[]> =>
    request<Education[]>('/education/reorder', {
      method: 'POST',
      body: JSON.stringify({ reorders }),
    }),
};

// ── Templates ───────────────────────────────────────────────────────────────

export interface Template {
  id: string;
  userId: string;
  filename: string;
  createdAt: string;
  updatedAt: string;
}

export const templateApi = {
  /**
   * Get user's template metadata (filename, dates), or null if no template.
   */
  get: (): Promise<Template | null> =>
    request<Template | null>('/templates'),

  /**
   * Upload or replace user's template.
   * Sends multipart form data with the file.
   */
  upload: async (file: File): Promise<Template> => {
    const formData = new FormData();
    formData.append('template', file);

    const res = await fetch(`${BASE_URL}/templates`, {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });

    if (!res.ok) {
      let errorBody: { error?: string } = {};
      try {
        errorBody = (await res.json()) as { error?: string };
      } catch {
        // ignore
      }
      throw new ApiError(
        res.status,
        errorBody.error ?? 'Upload failed',
        `Template upload failed with status ${res.status}`,
      );
    }

    return res.json() as Promise<Template>;
  },

  /**
   * Download user's uploaded template file.
   * Triggers a browser download.
   */
  download: async (): Promise<void> => {
    const res = await fetch(`${BASE_URL}/templates/download`, {
      credentials: 'include',
    });

    if (!res.ok) {
      let msg = `Download failed (${res.status})`;
      try {
        const body = (await res.json()) as { error?: string };
        if (body.error) msg = body.error;
      } catch {
        // ignore
      }
      throw new Error(msg);
    }

    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');

    // Get filename from Content-Disposition header
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const match = /filename="([^"]+)"/.exec(disposition);
    a.download = match?.[1] ?? 'resume-template.docx';
    a.href = url;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  /**
   * Download the default template.
   * This is served as a static file from the frontend.
   */
  downloadDefault: async (): Promise<void> => {
    const a = document.createElement('a');
    a.href = '/default-template.docx';
    a.download = 'resume-template.docx';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  },

  /**
   * Delete user's template.
   */
  delete: (): Promise<null> =>
    request<null>('/templates', { method: 'DELETE' }),
};

// ── Credits ─────────────────────────────────────────────────────────────────

export const creditsApi = {
  getBalance: (): Promise<UserPlan> => request<UserPlan>('/credits/balance'),

  getLedger: (limit?: number, offset?: number): Promise<CreditLedgerEntry[]> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();
    return request<CreditLedgerEntry[]>(`/credits/ledger${query ? `?${query}` : ''}`);
  },
};

// ── Applications ───────────────────────────────────────────────────────────────

export interface ApplicationSankeyLink {
  source: string;
  target: string;
  value: number;
}

export interface ApplicationSankeyData {
  links: ApplicationSankeyLink[];
}

export const applicationApi = {
  list: (): Promise<Application[]> =>
    request<Application[]>('/applications'),

  getById: (id: string): Promise<Application> =>
    request<Application>(`/applications/${id}`),

  create: (data: CreateApplication): Promise<Application> =>
    request<Application>('/applications', { method: 'POST', body: JSON.stringify(data) }),

  update: (id: string, data: UpdateApplication): Promise<Application> =>
    request<Application>(`/applications/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),

  delete: (id: string): Promise<null> =>
    request<null>(`/applications/${id}`, { method: 'DELETE' }),

  getSankeyData: (): Promise<ApplicationSankeyData> =>
    request<ApplicationSankeyData>('/applications/sankey'),

  listEvents: (applicationId: string): Promise<ApplicationEvent[]> =>
    request<ApplicationEvent[]>(`/applications/${applicationId}/events`),

  updateEvent: (applicationId: string, eventId: string, data: UpdateApplicationEvent): Promise<ApplicationEvent> =>
    request<ApplicationEvent>(`/applications/${applicationId}/events/${eventId}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    }),

  deleteEvent: (applicationId: string, eventId: string): Promise<null> =>
    request<null>(`/applications/${applicationId}/events/${eventId}`, { method: 'DELETE' }),
};

// ── Company metadata ───────────────────────────────────────────────────────────

export const companyApi = {
  get: (companyName: string): Promise<{ portalUrl: string | null }> =>
    request<{ portalUrl: string | null }>(
      `/applications/companies/${encodeURIComponent(companyName)}`,
    ),

  upsert: (companyName: string, portalUrl: string | null): Promise<null> =>
    request<null>(`/applications/companies/${encodeURIComponent(companyName)}`, {
      method: 'PUT',
      body: JSON.stringify({ portalUrl }),
    }),

  delete: (companyName: string): Promise<null> =>
    request<null>(`/applications/companies/${encodeURIComponent(companyName)}`, {
      method: 'DELETE',
    }),
};

// ── Admin ──────────────────────────────────────────────────────────────────────

export interface AdminJob {
  id: string;
  name: string;
  state: string;
  data: unknown;
  output: unknown;
  createdOn: string;
  startedOn: string | null;
  completedOn: string | null;
}

export const adminApi = {
  listUsers: (limit?: number, offset?: number): Promise<{ users: (User & { plan: UserPlan })[]; total: number }> => {
    const params = new URLSearchParams();
    if (limit) params.append('limit', limit.toString());
    if (offset) params.append('offset', offset.toString());
    const query = params.toString();
    return request<{ users: (User & { plan: UserPlan })[]; total: number }>(`/admin/users${query ? `?${query}` : ''}`);
  },

  findUserByEmail: (email: string): Promise<User & { plan: UserPlan }> =>
    request<User & { plan: UserPlan }>(`/admin/users?email=${encodeURIComponent(email)}`),

  grantCredits: (userId: string, amount: number, note?: string): Promise<{ message: string }> =>
    request<{ message: string }>(`/admin/users/${userId}/grant-credits`, {
      method: 'POST',
      body: JSON.stringify({ amount, note }),
    }),

  setSubscription: (userId: string, tier: string, monthlyCredits: number): Promise<{ message: string }> =>
    request<{ message: string }>(`/admin/users/${userId}/set-subscription`, {
      method: 'POST',
      body: JSON.stringify({ tier, monthlyCredits }),
    }),

  listJobs: (opts?: { state?: string; name?: string; hours?: number; limit?: number; offset?: number }): Promise<{ jobs: AdminJob[]; total: number }> => {
    const params = new URLSearchParams();
    if (opts?.state) params.append('state', opts.state);
    if (opts?.name) params.append('name', opts.name);
    if (opts?.hours) params.append('hours', opts.hours.toString());
    if (opts?.limit) params.append('limit', opts.limit.toString());
    if (opts?.offset) params.append('offset', opts.offset.toString());
    const query = params.toString();
    return request<{ jobs: AdminJob[]; total: number }>(`/admin/jobs${query ? `?${query}` : ''}`);
  },
};

// ── Shared constants ────────────────────────────────────────────────────────

export { CREDIT_COSTS };
export { APPLICATION_ACTION_LABELS } from '@resume-builder/shared';

export type {
  Application,
  ApplicationAction,
  ApplicationEvent,
  CreateApplication,
  UpdateApplication,
  UpdateApplicationEvent,
  User,
  AuthResponse,
  Tenure,
  CreateTenure,
  UpdateTenure,
  Title,
  CreateTitle,
  UpdateTitle,
  ReorderTitleItem,
  Experience,
  CreateExperience,
  UpdateExperience,
  ReorderExperienceItem,
  Capability,
  CreateCapability,
  UpdateCapability,
  ReorderItem,
  SkillBucket,
  SkillBucketListResponse,
  CreateSkillBucket,
  UpdateSkillBucket,
  AssignSkill,
  ReorderSkillBucket,
  ReorderSkillBucketItem,
  JobPosting,
  IngestJobPostingResponse,
  Resume,
  CreateResume,
  CustomSectionSpec,
  Education,
  CreateEducation,
  UpdateEducation,
  ReorderEducationItem,
  UserPlan,
  CreditLedgerEntry,
};
